import React, { memo, useCallback, useEffect, useState } from "react";
import CountUp from "react-countup";
import { useNavigate, useSearchParams } from "react-router-dom";
import AccessRights from "../../components/AccessRights/AccessRights";
import AmountToggle from "../../components/AmountToggle/AmountToggle";
import Button, { ButtonIntentE } from "../../components/Button/Button";
import Icon from "../../components/Icon/Icon";
import OrderBy from "../../components/OrderBy/OrderBy";
import Search from "../../components/Search/Search";
import { H1, P } from "../../components/Text/Text";
import {
  AccessLevelE,
  IconColorE,
  IconSizeE,
  UserRoleE,
} from "../../models/enums";
import { useWizardCreator } from "../../popups/Wizard/useWizardCreator";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import { getLoggedUser } from "../../utils/helpers/tokenStorage";
import { usePagination } from "../../utils/hooks/usePagination";
import usePopup from "../../utils/hooks/usePopup";
import Pagination from "../../components/Pagination/Pagination";
import { WizardModeE, WizardTypeE } from "../../popups/Wizard/models";
import CampaignListItem from "./CampaignListItem";
import { useGetCampaignListQuery } from "../../api/campaignAPI";
import HiddenClick from "../../components/HiddenClick/HiddenClick";
import WhatsappHelp from "../../components/WhatsappHelp/WhatsappHelp";
import StatusTag from "../../components/StatusTag/StatusTag";
import { useWindowResize } from "../../utils/hooks/useWindowResize";
import { useAppSelector } from "../../store/hooks";
import { selectIsChangingAccount } from "../../store/selectors/authSelectors";

import styles from "./styles.module.scss";
import CampaignListSidebar from "./CampaignListSidebar";
import Sidebar from "../../components/Sidebar/Sidebar";

const ORDER_BY_OPTIONS = [
  { value: "num", label: "Campaign number" },
  { value: "name", label: "Name" },
];

const CampaignList = memo(() => {
  const user = getLoggedUser()?.user?.user;
  const [searchParams] = useSearchParams();
  const isChangingAccount = useAppSelector(selectIsChangingAccount);
  const {
    take,
    page,
    pages,
    next,
    prev,
    setPage,
    setTotalCount,
    totalCount,
    setTake,
  } = usePagination();
  const { createPopup } = usePopup();
  const createCampaign = useWizardCreator();
  const {
    data: {
      campaings_data: campaignListData = [],
      totalCount: campaignsTotalCount = 0,
    } = {},
    isFetching,
    isLoading,
  } = useGetCampaignListQuery(
    {
      itemPerPage: take,
      pageNumber: page - 1,
      sorting: searchParams.get("sorting"),
      filter: searchParams.get("filterType"),
      search: searchParams.get("filter"),
      actionNeed: searchParams.get("actionNeed"),
      statusName: searchParams.get("status"),
    },
    {
      // Skip query during account change to prevent duplicate requests
      skip: isChangingAccount,
    }
  );
  const navigate = useNavigate();
  const [showContextMenuIndex, setShowContextMenuIndex] = useState(-1);
  const noop = useCallback(() => {}, []);
  const [windowWidth] = useWindowResize();
  const [isSidebarHidden, setIsSidebarHidden] = useState(true);

  useEffect(() => {
    setIsSidebarHidden(true);
  }, [windowWidth]);

  const toggleSidebarVisibility = () => {
    setIsSidebarHidden((prev) => !prev);
  };

  // Return to the top of the scroll area on each new fetch (the pager sits at the end now).
  useEffect(() => {
    if (isFetching) {
      document.getElementById("app-scroll")?.scrollTo({ top: 0 });
    }
  }, [isFetching]);

  useEffect(() => {
    if (searchParams.get("wizard")) {
      navigateToCreateNewProject();
    }
  }, []);

  useEffect(() => {
    setTotalCount(campaignsTotalCount);
  }, [campaignsTotalCount]);

  // handle create new campaign button click
  const navigateToCreateNewProject = () => {
    const popupData = createCampaign({
      type: WizardTypeE.Campaign,
      mode: WizardModeE.Create,
    });
    createPopup(popupData);
  };

  const handleHiddenSpanClick = () => {
    navigate("/deliveries");
  };

  return (
    <>
      {!isMobileOrTablet() && <div className={styles.whatsappButtonMobileWrapper}>
        {/* <WhatsappHelp defaultCollapsed /> */}
      </div>}
      <HiddenClick action={handleHiddenSpanClick} id="hidden-span" />
      <div className={styles.sidebarWrapper} style={{ left: isSidebarHidden ? "-100vw" : 0 }}>
        <Sidebar>
          <Icon
            name="close"
            onClick={toggleSidebarVisibility}
            className={styles.closeSidebarButton}
            color={IconColorE.GRAY}
          />
          <CampaignListSidebar
            onAfterStatusSelect={() => setIsSidebarHidden(true)}
          />
        </Sidebar>
      </div>
      <main className={styles.mainWrapper}>
        <section className={styles.headerSection}>
          <div className={styles.headerTopSection} id="headerTopSection">
            <div className={styles.titleSection} id="titleSection">
              <div className={styles.filterIconWrapper}>
                <Icon
                  name="filter"
                  color={IconColorE.WHITE}
                  onClick={toggleSidebarVisibility}
                />
              </div>
              <H1>Campaigns</H1>
              {isFetching ? (
                <Icon
                  name="loading"
                  size={IconSizeE.S}
                  color={IconColorE.LIGHT_GRAY}
                />
              ) : (
                <span className={styles.projectCount}>
                  (<CountUp end={totalCount} duration={0.3} separator="," />)
                </span>
              )}
              <StatusTag />
            </div>
            <AccessRights
              accessFor={[UserRoleE.AdvertisingAgency, UserRoleE.Broadcaster]}
              condition={user.accessLevel !== AccessLevelE.GlobalAdmin}
            >
              <Button
                id="add-campaign-button"
                icon="add"
                intent={ButtonIntentE.CAMPAIGN}
                onClick={navigateToCreateNewProject}
                className={styles.addCampaignButton}
              >
                New campaign
              </Button>
            </AccessRights>
            <div id="search-campaign" className={styles.searchCampaign}>
              <Search formID="search-projects" withAutocomplete />
            </div>
          </div>
        </section>
        {!totalCount && !isLoading && (
          <P className={styles.resultNotFound}>No campaigns found</P>
        )}
        <div className={isLoading ? styles.loadingWrapper : ""}>
          <ul className={styles.campaignList}>
            {campaignListData.map((campaign, index) => (
              <CampaignListItem
                key={campaign.CampaignId}
                campaign={campaign}
                index={index}
                showContextMenuIndex={showContextMenuIndex}
                setShowContextMenuIndex={setShowContextMenuIndex}
                isLastElement={false}
                paginationElement={null}
                setSize={noop}
                windowWidth={windowWidth}
              />
            ))}
          </ul>
        </div>
        <div className={styles.pageFooter}>
          <AmountToggle changeAmount={setTake} labelText="" value={take} />
          <Pagination
            prev={prev}
            next={next}
            pages={pages}
            page={page}
            setPage={setPage}
          />
          <div className={styles.orderWrapper}>
            <OrderBy options={ORDER_BY_OPTIONS} />
          </div>
        </div>
      </main>
    </>
  );
});

export default CampaignList;
